import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { DetailWidgetInteractionItem, StyleProps } from '../WidgetElement';

const generateIframeStyle = (style: StyleProps, isShowWidget: boolean, radiusScale: number) => {
	const {
		transformSettings: { borderRadiusBottom, borderRadiusLeft, borderRadiusRight, borderRadiusTop },
	} = style;

	return {
		opacity: isShowWidget ? 1 : 0,
		border: 'none',
		borderTopRightRadius: `${Number(borderRadiusTop) * radiusScale}px`,
		borderBottomRightRadius: `${Number(borderRadiusRight) * radiusScale}px`,
		borderBottomLeftRadius: `${Number(borderRadiusBottom) * radiusScale}px`,
		borderTopLeftRadius: `${Number(borderRadiusLeft) * radiusScale}px`,
	};
};

type OverlayRect = { left: number; top: number; width: number; height: number };

const rectsDiffer = (a: OverlayRect | null, b: OverlayRect) =>
	!a ||
	Math.abs(a.left - b.left) > 0.5 ||
	Math.abs(a.top - b.top) > 0.5 ||
	Math.abs(a.width - b.width) > 0.5 ||
	Math.abs(a.height - b.height) > 0.5;

const Iframe = ({ id, style, data, widgetRef, position = 0 }: DetailWidgetInteractionItem) => {
	const {
		coordinate: { x, y },
		dimension: { w = 150, h = 100 },
	} = style;

	const centerPoint = {
		x: x + w - w / 2,
		y: y + h - h / 2,
	};

	const {
		start,
		end,
		settings: { url },
	} = data as any;

	let isShowWidget = position >= start && position < end;

	let renderUrl = url || 'https://qbrick.com';
	const isValidUrl = renderUrl.match('^(http|https)://');
	if (isValidUrl?.[0] !== 'http://' && isValidUrl?.[0] !== 'https://') {
		renderUrl = `https://${renderUrl}`;
	}

	// The <foreignObject> is only a measuring anchor: the real iframe is portalled out of the SVG at its
	// on-screen size, so the page is responsive and touch/clicks work on iOS and Safari.
	const foRef = useRef<SVGForeignObjectElement | null>(null);
	const setForeignRef = useCallback(
		(node: SVGForeignObjectElement | null) => {
			foRef.current = node;
			if (typeof widgetRef === 'function') {
				widgetRef(node);
			} else if (widgetRef) {
				(widgetRef as React.MutableRefObject<SVGForeignObjectElement | null>).current = node;
			}
		},
		[widgetRef]
	);

	const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
	const [overlayRect, setOverlayRect] = useState<OverlayRect | null>(null);

	useEffect(() => {
		const fo = foRef.current;
		const container = fo?.ownerSVGElement?.parentElement ?? null;
		setPortalTarget(container);
		if (!fo || !container) {
			return;
		}

		let raf = 0;
		const measure = () => {
			const foRect = fo.getBoundingClientRect();
			const cRect = container.getBoundingClientRect();
			const next: OverlayRect = {
				left: foRect.left - cRect.left,
				top: foRect.top - cRect.top,
				width: foRect.width,
				height: foRect.height,
			};
			setOverlayRect(prev => (rectsDiffer(prev, next) ? next : prev));
		};
		const update = () => {
			cancelAnimationFrame(raf);
			raf = requestAnimationFrame(measure);
		};

		update();
		const ro = new ResizeObserver(update);
		ro.observe(container);
		window.addEventListener('resize', update);
		window.addEventListener('orientationchange', update);
		window.addEventListener('scroll', update, true);
		document.addEventListener('fullscreenchange', update);
		document.addEventListener('webkitfullscreenchange', update);

		return () => {
			cancelAnimationFrame(raf);
			ro.disconnect();
			window.removeEventListener('resize', update);
			window.removeEventListener('orientationchange', update);
			window.removeEventListener('scroll', update, true);
			document.removeEventListener('fullscreenchange', update);
			document.removeEventListener('webkitfullscreenchange', update);
		};
	}, [x, y, w, h]);

	return (
		<>
			<g id={id} style={{ pointerEvents: 'none' }}>
				<foreignObject
					ref={setForeignRef}
					x={x}
					y={y}
					width={w}
					height={h}
					style={{ transformOrigin: `${centerPoint.x}px ${centerPoint.y}px`, position: 'relative' }}
				/>
			</g>
			{portalTarget &&
				overlayRect &&
				ReactDOM.createPortal(
					<iframe
						src={renderUrl}
						className="widget iframe-widget"
						style={{
							...generateIframeStyle(style, isShowWidget, overlayRect.width / w),
							position: 'absolute',
							left: overlayRect.left,
							top: overlayRect.top,
							width: overlayRect.width,
							height: overlayRect.height,
							pointerEvents: isShowWidget ? 'auto' : 'none',
							// Below the SVG, so widgets layered over the iframe stay visible and tappable.
							zIndex: -1,
						}}
					/>,
					portalTarget
				)}
		</>
	);
};

export default Iframe;
